import { useEffect, useRef, useState, useCallback } from "react";
import { useInView } from "react-intersection-observer";

const getScrollableParent = (element) => {
  let el = element;
  while (el) {
    const style = getComputedStyle(el);
    const overflow = style.overflow + style.overflowY;
    // Require both: (1) a CSS rule that would allow scrolling, AND (2) the
    // element actually has more content than it can show. Elements that
    // declare overflow:auto but inherit `height: auto` (e.g. PlatformBuilder's
    // StyledBody, whose parent grid has no defined row size) expand to fit
    // content and never scroll — using such an element as the IntersectionObserver
    // root keeps the sentinel permanently in-view, breaking lazy pagination.
    if (/(auto|scroll)/.test(overflow) && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
};

export const useElemInView = ({ callback, enabled = true }) => {
  const [scrollRoot, setScrollRoot] = useState(null);
  const { ref, inView, entry } = useInView({ 
    threshold: 0,
    root: scrollRoot,
  });

  const setRef = useCallback((node) => {
    ref(node);
    if (node) {
      const scrollParent = getScrollableParent(node);
      setScrollRoot(scrollParent);
    }
  }, [ref]);

  const lastTriggeredIdRef = useRef(null);

  useEffect(() => {
    if (!inView || !enabled || !entry?.target) return;
    if (!callback) return;

    const currentId = entry.target.id;
    if (currentId === lastTriggeredIdRef.current) return;

    lastTriggeredIdRef.current = currentId;
    callback();
  }, [inView, enabled, entry?.target?.id, callback]);

  // Reset the de-bounce key whenever the sentinel is not actively being
  // observed (either out of view, or the consumer disabled it — typically
  // mid-fetch). This is what lets autoload "fill the viewport": after each
  // fetch finishes, enabled flips true, the key is null, and the callback
  // fires again so long as the sentinel is still in view. Autoload stops
  // naturally once new rows push the sentinel out of the viewport (then it
  // re-arms on the user's next scroll-down).
  useEffect(() => {
    if (!inView || !enabled) {
      lastTriggeredIdRef.current = null;
    }
  }, [inView, enabled]);

  return { observerRef: setRef };
};
